import type { ReactNode } from "react";
import { Lock, Sparkles } from "lucide-react";
import { cn } from "@/lib/cn";
import { Panel } from "@/components/ui/Panel";
import { useFeature, useFeatures, usePlan } from "./hooks";

/**
 * El candado se enseña, no se esconde.
 *
 * Durante la mayor parte del proyecto un modulo no contratado respondia 404,
 * para no confirmar que existia. Eso tiene sentido en algo que protege datos;
 * aqui no protege nada —el catalogo de planes es publico y el limite real lo
 * pone la base— y a cambio deja al cliente sin saber que existe lo que podria
 * contratar. Se ve, con candado, y al pulsarlo se explica.
 */
export function LockBadge({ className }: { className?: string }) {
  return (
    <Lock
      className={cn("size-3 shrink-0 text-ink-3", className)}
      strokeWidth={2}
      aria-label="Necesita otro plan"
    />
  );
}

/**
 * Un boton que la tienda no puede usar. Sigue visible y sigue diciendo lo que
 * hace: es la unica forma de que alguien pregunte por el.
 */
export function LockedButton({
  featureKey,
  children,
  className,
}: {
  featureKey: string;
  children: ReactNode;
  className?: string;
}) {
  const features = useFeatures();
  const feature = (features.data ?? []).find((f) => f.key === featureKey);

  return (
    <button
      type="button"
      disabled
      title={
        feature
          ? `${feature.name} · disponible desde el plan Pro`
          : "Disponible en otro plan"
      }
      className={cn(
        "inline-flex cursor-not-allowed items-center gap-1.5 rounded-md border border-line",
        "bg-sunk px-3 py-2 text-ui font-medium text-ink-3",
        className,
      )}
    >
      <LockBadge />
      {children}
    </button>
  );
}

/**
 * Lo que se ve al entrar a una seccion que el plan no incluye.
 *
 * Dice que hace, no solo que falta: "contrata el plan Pro" no le explica a
 * nadie por que le conviene.
 */
export function LockedModule({ moduleKey, title }: { moduleKey: string; title: string }) {
  const plan = usePlan();
  const features = useFeatures();

  const delModulo = (features.data ?? []).filter((f) => f.moduleKey === moduleKey);

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-head font-semibold tracking-tight">{title}</h1>

      <Panel>
        <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
          <span className="grid size-11 place-items-center rounded-full bg-sunk">
            <Lock className="size-5 text-ink-3" strokeWidth={1.8} />
          </span>

          <div>
            <h2 className="text-emph font-semibold">{title} no está en tu plan</h2>
            <p className="mx-auto mt-1 max-w-[46ch] text-ui text-ink-2">
              {DESCRIPCIONES[moduleKey] ??
                "Esta sección forma parte de los planes con mensualidad."}
            </p>
          </div>

          {plan.data && (
            <p className="text-meta text-ink-3">
              Tu plan actual es <b className="text-ink-2">{plan.data.name}</b>.
            </p>
          )}

          {delModulo.length > 0 && (
            <div className="mt-2 w-full max-w-md rounded-md border border-line bg-sunk p-3 text-left">
              <p className="mb-2 flex items-center gap-1.5 text-meta font-semibold">
                <Sparkles className="size-3.5 text-accent" />
                Lo que incluye
              </p>
              <ul className="flex flex-col gap-1.5">
                {delModulo.map((f) => (
                  <li key={f.key} className="text-meta text-ink-2">
                    <b className="text-ink">{f.name}.</b>{" "}
                    {f.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

/** Lo que hace cada seccion, en una linea que se entienda sin saber de sistemas. */
const DESCRIPCIONES: Record<string, string> = {
  purchases:
    "Registrar lo que le compras a tus proveedores, con su costo, para que el inventario se llene solo y sepas cuánto ganas en cada venta.",
  suppliers: "Tu lista de proveedores, con sus datos, condiciones y el historial de lo que les compraste.",
  pos: "Vender desde el mostrador con lector de código de barras, descontando del mismo inventario que la web.",
  cash_register: "Apertura y cierre de caja por turno, con arqueo y diferencias.",
  marketing: "Cupones, promociones y descuentos por temporada.",
  invoicing: "Emitir boletas y facturas electrónicas desde el mismo pedido.",
  crm: "Seguimiento de clientes, recordatorios y campañas.",
  analytics_pro: "Rentabilidad por producto, comparativas por periodo y proyecciones.",
  multi_warehouse: "Más de un local o almacén, con stock y traslados entre ellos.",
  advanced_inventory: "Lotes, vencimientos, números de serie y tomas de inventario.",
};

/** Atajo para condicionar un trozo de pantalla a una capacidad del plan. */
export function IfFeature({
  featureKey,
  children,
  fallback = null,
}: {
  featureKey: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return <>{useFeature(featureKey) ? children : fallback}</>;
}
